import { type RouteObject } from 'react-router-dom';

import { Auth0ProviderWithNavigate } from '../auth0-provider';
import { RootErrorBoundary } from '../components/errors/RootErrorBoundary';
import { AdminLayout } from './layouts/AdminLayout';
import { lazyNamed } from './lazyNamed';

const AgentPage = lazyNamed(() => import('@features/admin-v2'), 'Agent');
const AnalyticsDocsPage = lazyNamed(() => import('@features/admin-v2'), 'AnalyticsDocs');
const AppointmentRedirectPage = lazyNamed(
  () => import('@features/admin-v2'),
  'AppointmentRedirect',
);
const BillingDashboardPage = lazyNamed(() => import('@features/admin-v2'), 'BillingDashboard');
const BillingDashboardDetailPage = lazyNamed(
  () => import('@features/admin-v2'),
  'BillingDashboardDetail',
);
const BillingTasksPage = lazyNamed(() => import('@features/admin-v2'), 'BillingTasks');
const BillingWorklistRedirectPage = lazyNamed(
  () => import('@features/admin-v2'),
  'BillingWorklistRedirect',
);
const BundleDetailPage = lazyNamed(() => import('@features/admin-v2'), 'BundleDetail');
const BundlesListPage = lazyNamed(() => import('@features/admin-v2'), 'BundlesList');
const CapabilitiesListPage = lazyNamed(() => import('@features/admin-v2'), 'CapabilitiesList');
const CapabilityDetailPage = lazyNamed(() => import('@features/admin-v2'), 'CapabilityDetail');
const ContainerEntitiesListPage = lazyNamed(
  () => import('@features/admin-v2'),
  'ContainerEntitiesList',
);
const ContainerEntityDetailPage = lazyNamed(
  () => import('@features/admin-v2'),
  'ContainerEntityDetail',
);
const CortexPoliciesPage = lazyNamed(() => import('@features/admin-v2'), 'CortexPolicies');
const CortexResourcesPage = lazyNamed(() => import('@features/admin-v2'), 'CortexResources');
const DataMigrationsListPage = lazyNamed(() => import('@features/admin-v2'), 'DataMigrationsList');
const DemoOrgProvisioningPage = lazyNamed(
  () => import('@features/admin-v2'),
  'DemoOrgProvisioning',
);
const EligibilityWorkqueuePage = lazyNamed(
  () => import('@features/admin-v2'),
  'EligibilityWorkqueue',
);
const EncounterRedirectPage = lazyNamed(() => import('@features/admin-v2'), 'EncounterRedirect');
const EncounterTelemetrySessionsListPage = lazyNamed(
  () => import('@features/admin-v2'),
  'EncounterTelemetrySessionsList',
);
const EvalResultDetailPage = lazyNamed(() => import('@features/admin-v2'), 'EvalResultDetail');
const EvalResultsListPage = lazyNamed(() => import('@features/admin-v2'), 'EvalResultsList');
const EventCatalogPage = lazyNamed(() => import('@features/admin-v2'), 'EventCatalog');
const FeatureExplorerPage = lazyNamed(() => import('@features/admin-v2'), 'FeatureExplorer');
const FeedbackDashboardPage = lazyNamed(() => import('@features/admin-v2'), 'FeedbackDashboard');
const FeedbackSessionsListPage = lazyNamed(
  () => import('@features/admin-v2'),
  'FeedbackSessionsList',
);
const FlatResourceRedirectPage = lazyNamed(
  () => import('@features/admin-v2'),
  'FlatResourceRedirect',
);
const ImportSessionDetailPage = lazyNamed(
  () => import('@features/admin-v2'),
  'ImportSessionDetail',
);
const IntakeHubAdminPage = lazyNamed(() => import('@features/admin-v2'), 'IntakeHubAdmin');
const McpAccessPage = lazyNamed(() => import('@features/admin-v2'), 'McpAccess');
const McpTokensPage = lazyNamed(() => import('@features/admin-v2'), 'McpTokens');
const ModuleEditPage = lazyNamed(() => import('@features/admin-v2'), 'ModuleEdit');
const ModuleGenerationSandboxPage = lazyNamed(
  () => import('@features/admin-v2'),
  'ModuleGenerationSandbox',
);
const MorningWorklistPage = lazyNamed(() => import('@features/admin-v2'), 'MorningWorklist');
const NoteProcessingSessionDetailPage = lazyNamed(
  () => import('@features/admin-v2'),
  'NoteProcessingSessionDetail',
);
const NoteProcessingSessionsListPage = lazyNamed(
  () => import('@features/admin-v2'),
  'NoteProcessingSessionsList',
);
const NoteRedirectPage = lazyNamed(() => import('@features/admin-v2'), 'NoteRedirect');
const NotFoundPage = lazyNamed(() => import('@features/admin-v2'), 'NotFound');
const OrganizationDetailPage = lazyNamed(() => import('@features/admin-v2'), 'OrganizationDetail');
const PayerConnectionsPage = lazyNamed(() => import('@features/admin-v2'), 'PayerConnections');
const PayerSyncPage = lazyNamed(() => import('@features/admin-v2'), 'PayerSync');
const ProspectDetailPage = lazyNamed(() => import('@features/admin-v2'), 'ProspectDetail');
const ProspectsListPage = lazyNamed(() => import('@features/admin-v2'), 'ProspectsList');
const ProviderDetailPage = lazyNamed(() => import('@features/admin-v2'), 'ProviderDetail');
const ProviderResourceListRedirect = lazyNamed(
  () => import('@features/admin-v2'),
  'ProviderResourceListRedirect',
);
const PulsesPage = lazyNamed(() => import('@features/admin-v2'), 'Pulses');
const RcmScoreboardPage = lazyNamed(() => import('@features/admin-v2'), 'RcmScoreboard');
const ResourceWorkspaceLayout = lazyNamed(
  () => import('@features/admin-v2'),
  'ResourceWorkspaceLayout',
);
const SalesScoreboardPage = lazyNamed(() => import('@features/admin-v2'), 'SalesScoreboard');
const ScopedResourceCatalogPage = lazyNamed(
  () => import('@features/admin-v2'),
  'ScopedResourceCatalog',
);
const ScopedResourceDetailPage = lazyNamed(
  () => import('@features/admin-v2'),
  'ScopedResourceDetail',
);
const ScopedResourceListPage = lazyNamed(() => import('@features/admin-v2'), 'ScopedResourceList');
const SearchPage = lazyNamed(() => import('@features/admin-v2'), 'Search');
const ShadowNotesListPage = lazyNamed(() => import('@features/admin-v2'), 'ShadowNotesList');
const StreamingThreadDetailPage = lazyNamed(
  () => import('@features/admin-v2'),
  'StreamingThreadDetail',
);
const StreamingThreadsListPage = lazyNamed(
  () => import('@features/admin-v2'),
  'StreamingThreadsList',
);
const SystemNotificationsPage = lazyNamed(
  () => import('@features/admin-v2'),
  'SystemNotifications',
);
const TemplateConversionOutputViewerPage = lazyNamed(
  () => import('@features/admin-v2'),
  'TemplateConversionOutputViewer',
);
const TemplateDetailPage = lazyNamed(() => import('@features/admin-v2'), 'TemplateDetail');
const TemplateModelConversionPage = lazyNamed(
  () => import('@features/admin-v2'),
  'TemplateModelConversion',
);
const TemplatesListPage = lazyNamed(() => import('@features/admin-v2'), 'TemplatesList');
const TranscriptRedirectPage = lazyNamed(() => import('@features/admin-v2'), 'TranscriptRedirect');
const VersionComparisonPage = lazyNamed(() => import('@features/admin-v2'), 'VersionComparison');
const AdminClinicDocumentsPage = lazyNamed(
  () => import('@features/clinic-documents'),
  'AdminClinicDocumentsPage',
);
const StaffIntakeStatusPage = lazyNamed(
  () => import('@features/intake-hub/StaffIntakeStatusPage'),
  'StaffIntakeStatusPage',
);
const AdminJotAuditDashboardPage = lazyNamed(
  () => import('@features/jotaudit'),
  'AdminJotAuditDashboardPage',
);
const EncounterAuditDetail = lazyNamed(() => import('@features/jotaudit'), 'EncounterAuditDetail');
const EncounterAuditsList = lazyNamed(() => import('@features/jotaudit'), 'EncounterAuditsList');
const NoteAuditDetail = lazyNamed(() => import('@features/jotaudit'), 'NoteAuditDetail');
const ProviderDetail = lazyNamed(() => import('@features/jotaudit'), 'ProviderDetail');
const RuleAnalyticsPage = lazyNamed(() => import('@features/jotaudit'), 'RuleAnalyticsPage');
const RulesManagementPage = lazyNamed(() => import('@features/rules'), 'AdminRulesManagementPage');

export const adminV2Routes: RouteObject[] = [
  {
    path: '/admin-v2',
    element: <AdminLayout />,
    errorElement: (
      <Auth0ProviderWithNavigate>
        <RootErrorBoundary />
      </Auth0ProviderWithNavigate>
    ),
    children: [
      {
        index: true,
        element: <SearchPage />,
      },
      {
        path: 'provider/:providerId',
        element: <ProviderDetailPage />,
      },
      {
        path: 'provider/:providerId/notes',
        children: [
          {
            index: true,
            element: <ProviderResourceListRedirect resourceType="note" />,
          },
          {
            path: ':noteId',
            element: <NoteRedirectPage />,
          },
        ],
      },
      {
        path: 'provider/:providerId/transcriptions',
        children: [
          {
            index: true,
            element: <ProviderResourceListRedirect resourceType="transcription_session" />,
          },
          {
            path: ':transcriptId',
            element: <TranscriptRedirectPage />,
          },
        ],
      },
      // Encounters and appointments render only through the resource shell
      // now (GDN-2723); these forward to their canonical org/user-scoped URLs.
      {
        path: 'provider/:providerId/encounters',
        children: [
          {
            index: true,
            element: <ProviderResourceListRedirect resourceType="encounter" />,
          },
          {
            path: ':encounterId',
            element: <EncounterRedirectPage />,
          },
        ],
      },
      {
        path: 'provider/:providerId/appointments',
        children: [
          {
            index: true,
            element: <ProviderResourceListRedirect resourceType="appointment" />,
          },
          {
            path: ':appointmentId',
            element: <AppointmentRedirectPage />,
          },
        ],
      },
      {
        path: 'provider/:providerId/resources',
        element: <ProviderResourceListRedirect />,
      },
      {
        path: 'provider/:providerId/note-processing-sessions',
        element: <NoteProcessingSessionsListPage />,
      },
      {
        path: 'provider/:providerId/streaming-threads',
        element: <StreamingThreadsListPage />,
      },
      {
        path: 'provider/:providerId/templates',
        element: <TemplatesListPage />,
      },
      {
        path: 'provider/:providerId/templates/:templateId/:templateVersion',
        element: <TemplateDetailPage />,
      },
      {
        path: 'provider/:providerId/templates/:templateId',
        element: <TemplateDetailPage />,
      },
      {
        path: 'provider/:providerId/templates/:templateId/compare',
        element: <VersionComparisonPage />,
      },
      {
        path: 'provider/:providerId/templates/:templateId/:templateVersion/module-edit',
        element: <ModuleEditPage />,
      },
      {
        path: 'templates/:templateId',
        element: <TemplateDetailPage />,
      },
      {
        path: 'templates/:templateId/compare',
        element: <VersionComparisonPage />,
      },
      {
        path: 'transcript/:id',
        element: <TranscriptRedirectPage />,
      },
      {
        path: 'encounter/:id',
        element: <EncounterRedirectPage />,
      },
      {
        path: 'appointment/:id',
        element: <AppointmentRedirectPage />,
      },
      {
        path: 'note/:noteId',
        element: <NoteRedirectPage />,
      },
      {
        path: 'organization/:organizationId',
        element: <OrganizationDetailPage />,
      },
      {
        path: 'organization/:organizationId/data-migrations',
        element: <DataMigrationsListPage />,
      },
      {
        path: 'organization/:organizationId/data-migrations/session/:sessionId',
        element: <ImportSessionDetailPage />,
      },
      // Worklist + events side by side (GDN-2774) is a section of the org
      // workspace, not a page — rendering it here cost the org rail. The URL
      // it shipped with forwards to that section, query string intact.
      {
        path: 'organization/:organizationId/billing-worklist',
        element: <BillingWorklistRedirectPage />,
      },
      // Canonical resource URLs (GDN-2723). Resources are always org-scoped,
      // so they nest under the organization; a `users/:userId` segment narrows
      // the same views to one member. Both levels share one layout because the
      // level is simply whether that segment is present.
      {
        path: 'organization/:organizationId/resources',
        element: <ResourceWorkspaceLayout />,
        children: [
          {
            index: true,
            element: <ScopedResourceCatalogPage />,
          },
          {
            path: ':resourceType',
            element: <ScopedResourceListPage />,
          },
          {
            path: ':resourceType/:resourceId',
            element: <ScopedResourceDetailPage />,
          },
        ],
      },
      {
        path: 'organization/:organizationId/users/:userId/resources',
        element: <ResourceWorkspaceLayout />,
        children: [
          {
            index: true,
            element: <ScopedResourceCatalogPage />,
          },
          {
            path: ':resourceType',
            element: <ScopedResourceListPage />,
          },
          {
            path: ':resourceType/:resourceId',
            element: <ScopedResourceDetailPage />,
          },
        ],
      },
      // Pre-consolidation flat explorer URLs. Still built by the data-import
      // review browser's committed-resource links (GDN-2723).
      {
        path: 'resources/:resourceType',
        element: <FlatResourceRedirectPage />,
      },
      {
        path: 'resources/:resourceType/:resourceId',
        element: <FlatResourceRedirectPage />,
      },
      {
        path: 'note-processing-sessions',
        element: <NoteProcessingSessionsListPage />,
      },
      {
        path: 'note-processing-session/:npsId',
        element: <NoteProcessingSessionDetailPage />,
      },
      {
        path: 'jotaudit/:organizationId',
        element: <AdminJotAuditDashboardPage />,
      },
      {
        path: 'jotaudit/:organizationId/note/:noteId',
        element: <NoteAuditDetail />,
      },
      {
        path: 'jotaudit/:organizationId/rules/:ruleId/analytics',
        element: <RuleAnalyticsPage />,
      },
      {
        path: 'jotaudit/:organizationId/provider/:userId',
        element: <ProviderDetail />,
      },
      {
        path: 'jotaudit/:organizationId/encounters',
        element: <EncounterAuditsList />,
      },
      {
        path: 'jotaudit/:organizationId/encounter/:encounterId',
        element: <EncounterAuditDetail />,
      },
      {
        path: 'rules',
        element: <RulesManagementPage />,
      },
      { path: 'intake-status', element: <StaffIntakeStatusPage /> },
      {
        path: 'intake-hub',
        element: <IntakeHubAdminPage />,
      },
      {
        path: 'cortex/policies',
        element: <CortexPoliciesPage />,
      },
      {
        path: 'cortex/resources',
        element: <CortexResourcesPage />,
      },
      {
        path: 'event-catalog',
        element: <EventCatalogPage />,
      },
      {
        path: 'feedback',
        element: <FeedbackDashboardPage />,
      },
      {
        path: 'feedback/sessions',
        element: <FeedbackSessionsListPage />,
      },
      {
        path: 'encounter-telemetry-sessions',
        element: <EncounterTelemetrySessionsListPage />,
      },
      {
        path: 'eval-results',
        element: <EvalResultsListPage />,
      },
      {
        path: 'eval-results/:resultId',
        element: <EvalResultDetailPage />,
      },
      {
        path: 'feature-explorer',
        element: <FeatureExplorerPage />,
      },
      {
        path: 'pulses',
        element: <PulsesPage />,
      },
      {
        path: 'system-notifications',
        element: <SystemNotificationsPage />,
      },
      {
        path: 'module-generation-sandbox/:moduleId?',
        element: <ModuleGenerationSandboxPage />,
      },
      {
        path: 'capabilities',
        element: <CapabilitiesListPage />,
      },
      {
        path: 'mcp-access',
        element: <McpAccessPage />,
      },
      {
        path: 'mcp-tokens',
        element: <McpTokensPage />,
      },
      {
        path: 'capability/:capabilityId',
        element: <CapabilityDetailPage />,
      },
      {
        path: 'bundles',
        element: <BundlesListPage />,
      },
      {
        path: 'bundle/:bundleId',
        element: <BundleDetailPage />,
      },
      {
        path: 'streaming-threads',
        element: <StreamingThreadsListPage />,
      },
      {
        path: 'streaming-thread/:threadId',
        element: <StreamingThreadDetailPage />,
      },
      {
        path: 'template-conversion-output-viewer',
        element: <TemplateConversionOutputViewerPage />,
      },
      {
        path: 'template-model-conversion',
        element: <TemplateModelConversionPage />,
      },
      {
        path: 'eligibility-workqueue',
        element: <EligibilityWorkqueuePage />,
      },
      {
        path: 'morning-worklist',
        element: <MorningWorklistPage />,
      },
      {
        path: 'rcm-scoreboard',
        element: <RcmScoreboardPage />,
      },
      {
        path: 'sales-scoreboard',
        element: <SalesScoreboardPage />,
      },
      {
        path: 'billing-dashboard',
        element: <BillingDashboardPage />,
      },
      {
        path: 'billing-dashboard/:organizationId',
        element: <BillingDashboardDetailPage />,
      },
      {
        path: 'billing-tasks',
        element: <BillingTasksPage />,
      },
      {
        path: 'payer-connections',
        element: <PayerConnectionsPage />,
      },
      {
        path: 'prospects',
        element: <ProspectsListPage />,
      },
      {
        path: 'prospect/:prospectSegmentId',
        element: <ProspectDetailPage />,
      },
      {
        path: 'payers/sync',
        element: <PayerSyncPage />,
      },
      {
        path: 'demo-org',
        element: <DemoOrgProvisioningPage />,
      },
      {
        path: 'provider/:providerId/containers/:containerKey',
        element: <ContainerEntitiesListPage />,
      },
      {
        path: 'provider/:providerId/containers/:containerKey/:entityId',
        element: <ContainerEntityDetailPage />,
      },
      {
        path: 'shadow-notes',
        element: <ShadowNotesListPage />,
      },
      {
        path: 'agent',
        element: <AgentPage />,
      },
      {
        path: 'analytics-docs',
        element: <AnalyticsDocsPage />,
      },
      {
        path: 'clinic-documents/:organizationId',
        element: <AdminClinicDocumentsPage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];
